import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useParams } from 'react-router'
import { Button } from '@/components/ui/button'
import { PlusIcon, SearchLgIcon } from '@/components/ui/icons'
import { Switch } from '@/components/ui/switch'
import { AddWalkInDialog } from '@/features/marquee/components/add-walk-in-dialog'
import {
	AssignHostDialog,
	type AssignHostTarget
} from '@/features/marquee/components/assign-host-dialog'
import { ChangeBlanketCtaDialog } from '@/features/marquee/components/change-blanket-cta-dialog'
import {
	CheckInList,
	type CheckInPending
} from '@/features/marquee/components/check-in-list'
import { CheckInStats } from '@/features/marquee/components/check-in-stats'
import { CheckInToast } from '@/features/marquee/components/check-in-toast'
import { CloseEventDialog } from '@/features/marquee/components/close-event-dialog'
import { IncompleteAccountsBanner } from '@/features/marquee/components/incomplete-accounts-banner'
import {
	MarkUnresolvableDialog,
	type UnresolvableTarget
} from '@/features/marquee/components/mark-unresolvable-dialog'
import { MarqueeTableCard } from '@/features/marquee/components/marquee-table-card'
import {
	type RosterFilterOption,
	RosterFilterSelect
} from '@/features/marquee/components/roster-filter-select'
import { SearchInput } from '@/features/marquee/components/search-input'
import { StartEventDialog } from '@/features/marquee/components/start-event-dialog'
import { useMinWidth } from '@/features/marquee/hooks/use-min-width'
import { useMobileSearchAnchor } from '@/features/marquee/hooks/use-mobile-search-anchor'
import { makeQueryKeys } from '@/features/marquee/query-keys'
import {
	attendeesQueryOptions,
	eventQueryOptions,
	operatorQueryOptions
} from '@/features/marquee/query-options'
import {
	attendeeMatchesFilters,
	attendeeMatchesQuery,
	type CheckInActionId,
	type CheckInDisplayStatus,
	checkedInRsvpShare,
	checkInActionGates,
	checkInStatusToLabelKey,
	hasNoSalesforceAccount,
	incompleteAccountRows,
	invalidStateMessage,
	isCheckedIn,
	isMarketManagerAllowed,
	type RsvpFilterId,
	rsvpFilterOptions,
	walkInResolution
} from '@/features/marquee/utils'
import { errorHandler } from '@/helpers/utils'
import { useMarqueeEventDetailContext } from '@/layouts/marquee-event-detail-layout'
import {
	checkIn,
	type MarqueeAttendee,
	type MarqueeCheckInRequest,
	type MarqueeOperator,
	restoreResolution,
	undoCheckIn
} from '@/sdk'
import { useTranslation } from '@/translations/i18n'

/** Shown when the figure is genuinely unknown, never as a stand-in for zero. */
const STAT_PLACEHOLDER = '-'

/** Which row + action is in flight, so the spinner lands on the right button. */

const CHECK_IN_FILTER_OPTIONS: readonly RosterFilterOption<CheckInDisplayStatus>[] =
	[
		{ id: 'arrived', label: checkInStatusToLabelKey.arrived },
		{ id: 'no_show', label: checkInStatusToLabelKey.no_show },
		{ id: 'waiting', label: checkInStatusToLabelKey.waiting }
	]

/**
 * Which of the desk's own controls this screen shows, answered on both axes at
 * once: the lifecycle state that allows the write, and the role that may
 * perform it (FR51).
 *
 * The desk itself (check-in, undo, walk-ins, per-attendee CTAs) is open to both
 * roles and is nowhere in here. What a facilitator loses is the lifecycle
 * beside it and the blanket-CTA correction, which are the creator's.
 *
 * The names say "show", not "can", because that is what they decide: the whole
 * answer for whether the control is rendered, with nothing left to combine at
 * the call site. The state booleans are passed in rather than re-derived, so
 * this and the screen can never disagree about what `pre-start` means.
 */
const visibleLifecycleActions = ({
	isLive,
	isLocked,
	isPresentation,
	isPreStart,
	operator
}: {
	isLive: boolean
	isLocked: boolean
	isPresentation: boolean
	isPreStart: boolean
	operator: MarqueeOperator | undefined
}) => ({
	showChangeBlanketCta:
		isPresentation &&
		isLive &&
		isMarketManagerAllowed(operator, 'changeBlanketCta'),
	// Two renderings of the same button: inert until the event is locked, then
	// the real confirmation.
	showStartPlaceholder:
		isPreStart && isMarketManagerAllowed(operator, 'startEvent'),
	showStartButton: isLocked && isMarketManagerAllowed(operator, 'startEvent'),
	showCloseButton:
		!(isPreStart || isLocked) &&
		isMarketManagerAllowed(operator, 'closeEvent')
})

function MarqueeEventCheckIn() {
	const { t } = useTranslation()
	const { id } = useParams<{ id: string }>()
	const { stickyHeaderRef } = useMarqueeEventDetailContext()
	const [rsvpFilter, setRsvpFilter] = useState<RsvpFilterId[]>([])
	const [checkInFilter, setCheckInFilter] = useState<CheckInDisplayStatus[]>(
		[]
	)
	const [query, setQuery] = useState('')
	// Account completeness is a third, independent facet, the same switch the
	// attendees tab carries: it narrows the list on top of the RSVP and check-in
	// answers rather than instead of them, so it sits beside the two selects and
	// is never one of their values. The completeness banner turns it on — it is
	// the fix surface for what the banner warns about.
	const [onlyIncompleteAccounts, setOnlyIncompleteAccounts] = useState(false)
	const [pending, setPending] = useState<CheckInPending>(null)
	const [assignHostTarget, setAssignHostTarget] =
		useState<AssignHostTarget | null>(null)
	const [unresolvableTarget, setUnresolvableTarget] =
		useState<UnresolvableTarget | null>(null)
	// On mobile the search collapses into an icon button beside the list title;
	// opening it hides the title so the input can take the whole row.
	const isDesktop = useMinWidth('l')
	const [isSearchOpen, setIsSearchOpen] = useState(false)
	const showMobileSearch = !isDesktop && isSearchOpen

	const { data: event } = useQuery(eventQueryOptions(id))
	const isLive = event?.state === 'live'
	const isLocked = event?.state === 'locked'
	// Only a presentation carries a blanket CTA set. Stays false while the event
	// loads, so the action appears once we know the mode rather than flashing in
	// and out on a tune-up.
	const isPresentation = event?.capture_mode === 'presentation'

	// Two windows, not one: the desk is open while the event is live, the roster
	// fix actions answer in every state but `locked`.
	const gates = useMemo(
		() => checkInActionGates(event?.state),
		[event?.state]
	)
	// pre_work (or still loading): Start is shown but disabled until the event
	// is locked.
	const isPreStart = !(isLive || isLocked || event?.state === 'closed')

	const { data: operator } = useQuery(operatorQueryOptions())
	const {
		showChangeBlanketCta,
		showStartPlaceholder,
		showStartButton,
		showCloseButton
	} = visibleLifecycleActions({
		isLive,
		isLocked,
		isPresentation,
		isPreStart,
		operator
	})

	const {
		data: attendees,
		isLoading,
		isError
	} = useQuery(attendeesQueryOptions(id))

	const queryClient = useQueryClient()

	// Every row action invalidates the same prefix: the roster row changes and
	// so do the header counts on the event detail. Each carries its own toast
	// message in the variables, since one endpoint backs several labelled
	// actions (undo / undo_no_show both POST check-in/undo).
	const invalidateEvent = () =>
		queryClient.invalidateQueries({
			queryKey: makeQueryKeys.marquee.events.detail(id ?? '')
		})
	const settle = () => setPending(null)

	/**
	 * Walks back an unresolvable mark from the desk. The mark itself is made on the
	 * Export tab, which does not open until the event closes, so without this a row
	 * written off mid-event could not be reopened until the end of the day.
	 */
	const restore = useMutation({
		mutationFn: async ({ attendee }: { attendee: MarqueeAttendee }) => {
			const response = await restoreResolution({
				path: { attendeeId: attendee.id }
			})
			return response.data
		},
		onSuccess: async (_data, { attendee }) => {
			await queryClient.invalidateQueries({
				queryKey: makeQueryKeys.marquee.events.detail(id ?? '')
			})
			settle()
			toast.success(
				t('marquee:checkin_toast_restored', { name: attendee.name })
			)
		},
		onError: (error) => {
			settle()
			// `restore-resolution` shares the 409 `invalid-state` code with seven
			// other writes, so the backend's sentence names no action. Only
			// `locked` refuses this one, which is what the message says.
			const refusal = invalidStateMessage({
				error,
				action: 'restore_resolution',
				eventState: event?.state
			})
			if (refusal) {
				toast.error(
					t(refusal.key, {
						state: refusal.stateKey ? t(refusal.stateKey) : ''
					})
				)
				return
			}
			errorHandler(error)
		}
	})

	const check = useMutation({
		mutationFn: ({
			attendee,
			payload
		}: {
			attendee: MarqueeAttendee
			payload: MarqueeCheckInRequest
			message: string
		}) => checkIn({ path: { attendeeId: attendee.id }, body: payload }),
		onSuccess: async (_data, { attendee, message }) => {
			await invalidateEvent()
			showCheckInToast(attendee, message)
		},
		onError: (error) => errorHandler(error),
		onSettled: settle
	})

	const undo = useMutation({
		mutationFn: ({
			attendee
		}: {
			attendee: MarqueeAttendee
			message: string
		}) => undoCheckIn({ path: { attendeeId: attendee.id } }),
		onSuccess: async (_data, { message }) => {
			await invalidateEvent()
			toast.success(message)
		},
		onError: (error) => errorHandler(error),
		onSettled: settle
	})

	const rows = useMemo(() => attendees?.rows ?? [], [attendees?.rows])

	// The roster is a flat list; the header facets + search filter it client-side.
	const filteredRows = useMemo(() => {
		const q = query.trim().toLowerCase()
		if (
			rsvpFilter.length === 0 &&
			checkInFilter.length === 0 &&
			!onlyIncompleteAccounts &&
			!q
		) {
			return rows
		}
		return rows.filter(
			(row) =>
				attendeeMatchesFilters(row, {
					rsvp: rsvpFilter,
					checkIn: checkInFilter
				}) &&
				(!onlyIncompleteAccounts || hasNoSalesforceAccount(row)) &&
				(!q || attendeeMatchesQuery(row, q))
		)
	}, [rows, rsvpFilter, checkInFilter, onlyIncompleteAccounts, query])
	// Non-walk-in rows with no Salesforce account at all: what silently drops
	// out of the CSV. The same roster rule the attendees tab counts, so the two
	// tabs warn about the same number.
	const incompleteAccountCount = useMemo(
		() => incompleteAccountRows(rows),
		[rows]
	)

	// Keep the page from jumping while the mobile search filters the roster.
	const { cardRef, spacerRef, bottomSpacer, onSearchFocus, onSearchBlur } =
		useMobileSearchAnchor({
			active: showMobileSearch,
			stickyHeaderRef,
			measureKey: filteredRows
		})

	// Clears the other narrowings on the way in, so the list shows the rows the
	// banner counted and not their intersection with whatever facet or search
	// happened to be active — a smaller list there reads as "the rest are already
	// fixed", which is the silent failure this warning exists to prevent.
	const handleReviewIncomplete = () => {
		setQuery('')
		// Collapse the mobile search along with the term it held: left open it
		// covers the list title with an empty field, and the list narrows from a
		// switch the operator cannot see from there.
		setIsSearchOpen(false)
		setRsvpFilter([])
		setCheckInFilter([])
		setOnlyIncompleteAccounts(true)
		// Below the breakpoint the list sits off-screen under the banner, so
		// filtering it alone looks like nothing happened. Deferred a frame so the
		// scroll targets the card as it is once the narrowed list has rendered.
		requestAnimationFrame(() => {
			cardRef.current?.scrollIntoView({
				behavior: 'smooth',
				block: 'start'
			})
		})
	}

	const checkedInCount = rows.filter(isCheckedIn).length
	const rsvpShare = useMemo(() => checkedInRsvpShare(rows), [rows])
	const walkIns = useMemo(() => walkInResolution(rows), [rows])

	const stats = [
		{
			value: String(checkedInCount),
			valueSuffix: event?.invited ? `/${event.invited}` : undefined,
			label: t('marquee:checkin_stat_checked_in'),
			// Reads `-` rather than 0% when nobody confirmed: with no denominator
			// the caption has nothing to state.
			sublabel:
				rsvpShare === null
					? STAT_PLACEHOLDER
					: t('marquee:checkin_stat_checked_in_caption', {
							percent: rsvpShare
						})
		},
		{
			value: String(walkIns.total),
			label: t('marquee:checkin_stat_walk_ins'),
			// The third lane only appears once it exists, so the usual event shows
			// the two-part caption the design specifies.
			sublabel: t(
				walkIns.unresolvable > 0
					? 'marquee:checkin_stat_walk_ins_caption_unresolvable'
					: 'marquee:checkin_stat_walk_ins_caption',
				walkIns
			)
		}
	]

	// Confirmation toast carrying the inline "Undo" action. The undo reverts the
	// attendee to "waiting" (POST /check-in/undo) — a decision (arrived/no_show)
	// is otherwise final, so this is the only window to correct a mis-tap.
	const showCheckInToast = (attendee: MarqueeAttendee, title: string) => {
		toast.custom(
			(tst) => (
				<CheckInToast
					onDismiss={() => toast.dismiss(tst.id)}
					onUndo={() => {
						toast.dismiss(tst.id)
						undo.mutate({
							attendee,
							message: t('marquee:checkin_toast_undone', {
								name: attendee.name
							})
						})
					}}
					title={title}
					visible={tst.visible}
				/>
			),
			// Give the operator a real window to catch a mis-tap before it closes.
			{ duration: 6000 }
		)
	}

	// Single dispatcher for every contextual row action (checkbox + kebab
	// sheet + desktop links). Each action id maps to one backend call; several
	// share an endpoint but keep distinct labels/toasts (e.g. undo /
	// undo_no_show both POST check-in/undo).
	const handleAction = (
		attendee: MarqueeAttendee,
		action: CheckInActionId
	) => {
		if (!id) {
			return
		}
		setPending({ id: attendee.id, action })

		switch (action) {
			// The two arrival paths are one call and differ only in what the
			// operator asserts about the arrival: `check_in` says "on time",
			// `check_in_late` says "late". Both are offered on a waiting row and
			// on a no-show row alike, so neither lane has to be reached through
			// the other. Both send `is_late` rather than omitting it, so a
			// re-check-in cannot leave a stale assertion behind (the upsert is
			// final-state-wins and an absent flag clears).
			case 'check_in':
			case 'check_in_late': {
				const late = action === 'check_in_late'

				check.mutate({
					attendee,
					payload: {
						status: 'arrived',
						// No resolved host ID: log the attendance anyway.
						log_anyway: !attendee.airbnb_host_id,
						is_late: late
					},
					// Distinct wording per lane: the two items sit next to each
					// other in the menu, so a shared toast would not tell the
					// operator which of them landed.
					message: t(
						late
							? 'marquee:checkin_toast_checked_in_late'
							: 'marquee:checkin_toast_checked_in',
						{ name: attendee.name }
					)
				})
				return
			}
			case 'no_show':
				check.mutate({
					attendee,
					payload: { status: 'no_show' },
					message: t('marquee:checkin_toast_no_show', {
						name: attendee.name
					})
				})
				return
			case 'undo':
			case 'undo_no_show':
				undo.mutate({
					attendee,
					message: t(
						action === 'undo'
							? 'marquee:checkin_toast_undone'
							: 'marquee:checkin_toast_restored',
						{ name: attendee.name }
					)
				})
				return
			case 'restore_resolution':
				restore.mutate({ attendee })
				return
			case 'mark_unresolvable':
				settle()
				setUnresolvableTarget({
					attendeeId: attendee.id,
					name: attendee.name,
					hostId: attendee.airbnb_host_id ?? undefined
				})
				return
			// The one action here that opens a surface instead of writing: the
			// row waits on the dialog, so the pending lock is released now or the
			// whole roster stays frozen behind it.
			case 'resolve_id_capture':
				settle()
				setAssignHostTarget({
					attendeeId: attendee.id,
					name: attendee.name
				})
				return
			default:
				settle()
		}
	}

	return (
		<div className='flex flex-col gap-6 l:px-10 px-4 pt-6 pb-10'>
			<title>{t('marquee:page_title_check_in')}</title>
			<CheckInStats stats={stats} />

			{/* The roster's completeness warning, the same one the attendees tab
			    draws: these rows reach no Salesforce account, so they will not
			    export. Shown in every state and it stays until the accounts are
			    filled in. */}
			{event && (
				<IncompleteAccountsBanner
					count={incompleteAccountCount}
					onReview={handleReviewIncomplete}
				/>
			)}

			{/* Toolbar: search on the left, walk-in + close anchored right. The
			    buttons carry the anchoring themselves (`ml-auto`) rather than the
			    row spacing them apart, so they stay put on the frame before the
			    breakpoint hook has decided whether the search renders. */}
			<div className='flex l:flex-row flex-col l:items-center gap-3'>
				{isDesktop && (
					<SearchInput
						className='l:w-[345px]'
						clearLabel={t('common:clear_search')}
						onChange={setQuery}
						placeholder={t('marquee:attendee_search_placeholder')}
						value={query}
					/>
				)}
				<div className='l:ml-auto flex m:flex-row flex-col gap-3'>
					<AddWalkInDialog
						eventId={id}
						trigger={
							<Button
								className='m:w-auto w-full l:flex-none m:flex-1 whitespace-nowrap'
								disabled={!isLive || isLoading}
								size='md'
								type='button'
								variant='secondary'
							>
								<PlusIcon />
								{t('marquee:checkin_add_walk_in')}
							</Button>
						}
					/>
					{/* Presentation-only, and live-only: the blanket set is what
					    every check-in snapshots, and the event form that edits it
					    is pre_work-only, so from the lock onward this is the only
					    way to correct it. A tune-up has no blanket set, so the
					    action is absent there rather than disabled. */}
					{showChangeBlanketCta && (
						<ChangeBlanketCtaDialog
							ctas={event?.ctas}
							eventId={id}
							eventState={event?.state}
							trigger={
								<Button
									className='m:w-auto w-full l:flex-none m:flex-1 whitespace-nowrap'
									disabled={isLoading}
									size='md'
									type='button'
									variant='secondaryOutline'
								>
									{t('marquee:checkin_change_blanket_cta')}
								</Button>
							}
						/>
					)}
					{/* Primary action tracks the lifecycle: Start event (disabled
					    in pre_work, armed once locked) until the event goes live,
					    then Close event. */}
					{showStartPlaceholder && (
						<Button
							className='m:w-auto w-full l:flex-none m:flex-1 whitespace-nowrap'
							disabled
							size='md'
							type='button'
							variant='primary'
						>
							{t('marquee:start_event')}
						</Button>
					)}
					{showStartButton && (
						<StartEventDialog
							eventId={id}
							trigger={
								<Button
									className='m:w-auto w-full l:flex-none m:flex-1 whitespace-nowrap'
									disabled={isLoading}
									size='md'
									type='button'
									variant='primary'
								>
									{t('marquee:start_event')}
								</Button>
							}
						/>
					)}
					{showCloseButton && (
						<CloseEventDialog
							eventId={id}
							trigger={
								<Button
									className='m:w-auto w-full l:flex-none m:flex-1 whitespace-nowrap'
									disabled={!isLive || isLoading}
									size='md'
									type='button'
									variant='primary'
								>
									{t('marquee:checkin_close_event')}
								</Button>
							}
						/>
					)}
				</div>
			</div>

			<MarqueeTableCard
				header={
					<div className='flex l:flex-row flex-col l:items-center l:justify-between gap-4'>
						<div className='flex l:w-auto w-full items-center gap-2'>
							{!showMobileSearch && (
								<h2 className='min-w-0 flex-1 font-semibold text-lg text-neutral-900'>
									{t('marquee:checkin_list_title')}
								</h2>
							)}
							{showMobileSearch && (
								<SearchInput
									autoFocus
									className='flex-1'
									clearLabel={t('common:clear_search')}
									onBlur={onSearchBlur}
									onChange={setQuery}
									onDismiss={() => setIsSearchOpen(false)}
									onFocus={onSearchFocus}
									placeholder={t(
										'marquee:attendee_search_placeholder'
									)}
									value={query}
								/>
							)}
							{!(isDesktop || showMobileSearch) && (
								<Button
									aria-label={t(
										'marquee:attendee_search_placeholder'
									)}
									onClick={() => setIsSearchOpen(true)}
									shape='square'
									size='md'
									type='button'
									variant='secondaryOutline'
								>
									<SearchLgIcon />
								</Button>
							)}
						</div>
						{/* Wraps below the breakpoint, where the header is already a
						    column and the two facets sit under the title. */}
						<div className='flex flex-wrap items-center gap-2'>
							<RosterFilterSelect
								label='marquee:roster_filter_rsvp_status'
								onChange={setRsvpFilter}
								options={rsvpFilterOptions}
								value={rsvpFilter}
							/>
							<RosterFilterSelect
								label='marquee:roster_filter_check_in_status'
								onChange={setCheckInFilter}
								options={CHECK_IN_FILTER_OPTIONS}
								value={checkInFilter}
							/>
							{/* Offered whenever the warning that turns it on
							    exists. Stays put while it is on even once the
							    count reaches zero: fixing the last row is exactly
							    what the filter leads to, and withdrawing the
							    control there would strand the operator on an
							    empty list with no way to switch it off. */}
							{(incompleteAccountCount > 0 ||
								onlyIncompleteAccounts) && (
								<Switch
									checked={onlyIncompleteAccounts}
									label={t(
										'marquee:attendee_filter_no_account'
									)}
									onChange={setOnlyIncompleteAccounts}
								/>
							)}
						</div>
					</div>
				}
				ref={cardRef}
			>
				<CheckInList
					captureMode={event?.capture_mode}
					data={filteredRows}
					eventId={id}
					gates={gates}
					isError={isError}
					isLoading={isLoading}
					onAction={handleAction}
					pending={pending}
					timeZone={event?.time_zone}
				/>
			</MarqueeTableCard>

			{/* The same pair as the Export tab: the confirmation opens over the
			    search, so cancelling there goes back to looking rather than out. */}
			<AssignHostDialog
				eventId={id}
				eventState={event?.state}
				onClose={() => setAssignHostTarget(null)}
				onMarkUnresolvable={(handover) =>
					setUnresolvableTarget({
						attendeeId: handover.attendeeId,
						name: handover.name
					})
				}
				target={assignHostTarget}
			/>

			<MarkUnresolvableDialog
				eventId={id}
				eventState={event?.state}
				onClose={() => setUnresolvableTarget(null)}
				onMarked={() => setAssignHostTarget(null)}
				target={unresolvableTarget}
			/>

			{/* Holds the page height steady while the mobile search filters the
			    roster, so the body scroll doesn't jump. Sits below everything, so
			    the reserved space is off-screen. */}
			{bottomSpacer > 0 && (
				<div
					aria-hidden='true'
					ref={spacerRef}
					style={{ height: bottomSpacer }}
				/>
			)}
		</div>
	)
}

export { MarqueeEventCheckIn }
